"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";

import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FlowFooter } from "./flow-footer";
import { FlowWizardShell, type FlowWizardBrand } from "./flow-wizard-shell";
import { PortalTrustNote } from "./portal-trust-note";

export function PortalEmailVerification({
  token,
  email,
  contactName,
  brand,
  onVerified,
  purpose,
}: {
  token: string;
  email: string;
  contactName?: string | null;
  brand: FlowWizardBrand;
  onVerified: () => void;
  purpose?: "seller";
}) {
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);
  const [phase, setPhase] = useState<"email" | "code">("email");
  const [code, setCode] = useState("");
  const [kind, setKind] = useState<"LOGIN" | "CREATE_ACCOUNT">("LOGIN");
  const [error, setError] = useState<string | null>(null);
  const autoSubmittedCodeRef = useRef<string | null>(null);

  const sendCode = api.os.clientPortal.sendVerificationCode.useMutation({
    onSuccess: (result) => {
      setKind(result.kind);
      setError(null);
      setPhase("code");
    },
    onError: (mutationError) => setError(mutationError.message),
  });
  const verifyCode = api.os.clientPortal.verifyCode.useMutation({
    onSuccess: onVerified,
    onError: (mutationError) => {
      // Wait for the seller to change the code before attempting it again.
      setError(mutationError.message);
    },
  });

  useEffect(() => {
    if (
      phase !== "code" ||
      code.length !== 6 ||
      autoSubmittedCodeRef.current === code ||
      verifyCode.isPending
    ) {
      return;
    }
    autoSubmittedCodeRef.current = code;
    (
      document.getElementById(
        "portal-verification-code-form",
      ) as HTMLFormElement | null
    )?.requestSubmit();
  }, [code, phase, verifyCode.isPending]);

  const handleCodeSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (code.length !== 6 || verifyCode.isPending) return;
    setError(null);
    verifyCode.mutate({ token, code, kind });
  };

  const title =
    purpose === "seller"
      ? `Your next step with ${brand.name}`
      : `${brand.name} secure intake`;
  if (phase === "email") {
    return (
      <FlowWizardShell
        title={title}
        brand={brand}
        chrome="lead"
        stepDescription="Send a verification code to the email on this invite."
        footer={
          <FlowFooter
            hideBack
            primaryLabel={
              sendCode.isPending ? "Sending…" : "Send verification code"
            }
            primaryDisabled={!ready || sendCode.isPending}
            onPrimary={() =>
              sendCode.mutate({
                token,
              })
            }
          />
        }
      >
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {purpose === "seller"
                ? "Continue with your broker"
                : contactName
                  ? `Welcome, ${contactName.split(" ")[0]}`
                  : "Let’s get started"}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {purpose === "seller"
                ? "Confirm it’s you to see what your broker needs. We’ll email you a code. No password needed."
                : "We’ll send a one-time code to the email on your invite."}
            </p>
          </div>
          <div className="rounded-xl border bg-muted/30 px-4 py-3">
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
              Invited email
            </p>
            <p className="mt-1 text-[15px] font-medium">{email}</p>
            <p className="mt-1 text-[12px] text-muted-foreground">
              Your code will only be sent to this email.
            </p>
          </div>
          <PortalTrustNote brokerName={brand.name} />
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>
      </FlowWizardShell>
    );
  }

  return (
    <FlowWizardShell
      title={title}
      brand={brand}
      chrome="lead"
      stepDescription="Enter the verification code sent to your email."
      footer={
        <FlowFooter
          hideBack
          primaryLabel={verifyCode.isPending ? "Verifying…" : "Continue"}
          primaryDisabled={code.length !== 6 || verifyCode.isPending}
          onPrimary={() =>
            document
              .getElementById("portal-verification-code-form")
              ?.dispatchEvent(
                new Event("submit", { bubbles: true, cancelable: true }),
              )
          }
          secondaryAction={
            <Button
              type="button"
              variant="ghost"
              onClick={() => sendCode.mutate({ token })}
            >
              Resend code
            </Button>
          }
        />
      }
    >
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Check your email
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            We sent a six-digit code to {email}.
          </p>
        </div>
        <form
          id="portal-verification-code-form"
          onSubmit={handleCodeSubmit}
          className="space-y-3"
        >
          <label
            htmlFor="portal-verification-code"
            className="text-sm font-medium"
          >
            Verification code
          </label>
          <Input
            id="portal-verification-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(event) => {
              const nextCode = event.target.value
                .replace(/\D/g, "")
                .slice(0, 6);
              setCode(nextCode);
              if (nextCode.length < 6) autoSubmittedCodeRef.current = null;
              setError(null);
            }}
            placeholder="000000"
            autoFocus
            className="h-14 text-center font-mono text-2xl tracking-[0.4em]"
          />
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </form>
      </div>
    </FlowWizardShell>
  );
}
